/*
 * Metafield-driven "What's in the box".
 *
 * Renders the component list a variant carries in custom.variants_box /
 * custom.variants_box_qty, swapping it whenever the selected variant changes.
 *
 * Box components are routinely not published to the Online Store channel, so Liquid cannot
 * resolve them and snippets/product-box-contents.liquid can only server-render the subset it
 * can see. This file re-reads the same two metafields through the Storefront API — which
 * resolves against the Headless channel — and replaces the server-rendered data with the
 * complete list. Same fetch pattern as sections/shipping-info.liquid.
 *
 * Components must be active (not draft) and published to the Storefront API / Headless
 * channel, as must the parent product, or they come back empty here too.
 */
(function () {
  'use strict';

  const SELECTORS = {
    section: '[data-section-type="product"]',
    holder: '[data-box-contents]',
    data: '[data-box-contents-data]',
    list: '[data-box-contents-list]'
  };

  const API_URL = '/api/2024-10/graphql.json';
  const CACHE_PREFIX = 'lr-box-contents:v1:';
  const MAX_VARIANTS = 100;
  const MAX_COMPONENTS = 25;

  const QUERY = [
    'query BoxContents($handle: String!) {',
    '  product(handle: $handle) {',
    '    variants(first: ' + MAX_VARIANTS + ') {',
    '      nodes {',
    '        id',
    '        qty: metafield(namespace: "custom", key: "variants_box_qty") { value }',
    '        box: metafield(namespace: "custom", key: "variants_box") {',
    '          references(first: ' + MAX_COMPONENTS + ') {',
    '            nodes {',
    '              __typename',
    '              ... on ProductVariant {',
    '                title',
    '                image { url1x: url(transform: {maxWidth: 160, maxHeight: 160})',
    '                        url2x: url(transform: {maxWidth: 320, maxHeight: 320}) }',
    '                product {',
    '                  title',
    '                  featuredImage { url1x: url(transform: {maxWidth: 160, maxHeight: 160})',
    '                                  url2x: url(transform: {maxWidth: 320, maxHeight: 320}) }',
    '                }',
    '              }',
    '              ... on Product {',
    '                title',
    '                featuredImage { url1x: url(transform: {maxWidth: 160, maxHeight: 160})',
    '                                url2x: url(transform: {maxWidth: 320, maxHeight: 320}) }',
    '              }',
    '            }',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  const escapeHtml = (str) => String(str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  // Mirrors snippets/box-component-variant-title.liquid: drop "Default Title" and any
  // "No ..." exclusion segment, so "Universal | No MotoCAN V2" reads as just "Universal".
  function cleanVariantTitle(title) {
    if (!title || title === 'Default Title') return '';
    return String(title)
      .split('|')
      .map((segment) => segment.trim())
      .filter((segment) => segment && segment.split(' ')[0].toLowerCase() !== 'no')
      .join(' | ');
  }

  // Mirrors snippets/box-component-title.liquid: drop the "Spare Parts" merchandising
  // label and whatever separator it was sitting next to, keeping the original title if
  // stripping would leave nothing behind.
  function cleanComponentTitle(title) {
    if (!title) return '';

    const stripped = String(title)
      .replace(/spare\s+parts?/gi, ' ')
      .replace(/\s{2,}/g, ' ')
      .replace(/^[\s\-\u2013\u2014|:,]+/, '')
      .replace(/[\s\-\u2013\u2014|:,]+$/, '')
      .trim();

    return stripped || String(title).trim();
  }

  function itemHtml(item) {
    const media = item.image
      ? '<img src="' + escapeHtml(item.image) + '"' +
        ' srcset="' + escapeHtml(item.image) + ' 1x, ' + escapeHtml(item.image_2x || item.image) + ' 2x"' +
        ' alt="' + escapeHtml(item.title || '') + '" width="64" height="64" loading="lazy">'
      : '';

    const variantTitle = item.variant_title
      ? '<span class="box-contents__variant">' + escapeHtml(item.variant_title) + '</span>'
      : '';

    return '<li class="box-contents__item">' +
      '<div class="box-contents__media">' + media + '</div>' +
      '<div class="box-contents__info">' +
        '<span class="box-contents__name">' + escapeHtml(item.title || '') + '</span>' +
        variantTitle +
      '</div>' +
      '<span class="box-contents__qty">&times;' + escapeHtml(item.qty == null ? 1 : item.qty) + '</span>' +
    '</li>';
  }

  function render(holder, variantId) {
    const listEl = holder.querySelector(SELECTORS.list);
    if (!listEl) return;

    const components = variantId ? holder.__lrBoxData[String(variantId)] : null;

    if (!components || !components.length) {
      listEl.innerHTML = '';
      holder.hidden = true;
      return;
    }

    listEl.innerHTML = components.map(itemHtml).join('');
    holder.hidden = false;
  }

  // "gid://shopify/ProductVariant/123" -> "123", to match the ids Liquid emits.
  function idFromGid(gid) {
    const match = /(\d+)\s*$/.exec(String(gid || ''));
    return match ? match[1] : null;
  }

  // The quantity metafield is a list, so its value arrives as a JSON string.
  function parseQuantities(metafield) {
    if (!metafield || !metafield.value) return [];
    try {
      const parsed = JSON.parse(metafield.value);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (e) {
      return [];
    }
  }

  function componentFromNode(node, qty) {
    if (!node) return null;

    const isVariant = node.__typename === 'ProductVariant';
    const image = isVariant
      ? (node.image || (node.product && node.product.featuredImage))
      : node.featuredImage;

    return {
      title: cleanComponentTitle(isVariant ? node.product && node.product.title : node.title),
      variant_title: isVariant ? cleanVariantTitle(node.title) : '',
      image: image ? image.url1x : null,
      image_2x: image ? image.url2x : null,
      qty: qty == null ? 1 : qty
    };
  }

  function mapFromResponse(product) {
    const map = {};
    const variants = (product && product.variants && product.variants.nodes) || [];

    variants.forEach((variant) => {
      const nodes = variant.box && variant.box.references && variant.box.references.nodes;
      if (!nodes || !nodes.length) return;

      const id = idFromGid(variant.id);
      if (!id) return;

      const quantities = parseQuantities(variant.qty);
      const items = nodes
        .map((node, index) => componentFromNode(node, quantities[index]))
        .filter(Boolean);

      if (items.length) map[id] = items;
    });

    return map;
  }

  function readCache(handle) {
    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + handle);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeCache(handle, map) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + handle, JSON.stringify(map));
    } catch (e) {
      /* private mode / quota — the fetch simply repeats next page view */
    }
  }

  function fetchComponents(handle) {
    const token = window.STOREFRONT_TOKEN || 'a7660059af7c5be7c419f4c4ed390554';

    return fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query: QUERY, variables: { handle: handle } })
    })
      .then((res) => res.json())
      .then((res) => {
        if (res && res.errors) console.warn('[LR box contents] GraphQL errors:', res.errors);
        return mapFromResponse(res && res.data && res.data.product);
      });
  }

  function setup(holder) {
    if (holder.__lrBoxReady) return;
    holder.__lrBoxReady = true;

    const section = holder.closest(SELECTORS.section);
    if (!section) return;

    const dataEl = section.querySelector(SELECTORS.data);
    try {
      holder.__lrBoxData = dataEl ? JSON.parse(dataEl.textContent) : {};
    } catch (e) {
      console.error('[LR box contents] could not parse component data', e);
      holder.__lrBoxData = {};
    }

    // Kept current so a fetch that lands after a variant switch still paints the right list.
    holder.__lrVariantId = holder.getAttribute('data-box-contents-variant') || null;

    section.addEventListener('variantChange', (evt) => {
      const variant = evt.detail && evt.detail.variant;
      holder.__lrVariantId = variant ? String(variant.id) : null;
      render(holder, holder.__lrVariantId);
    });

    const handle = holder.getAttribute('data-box-contents-handle');
    if (!handle) return;

    // Whatever Liquid could resolve is already on screen; the Storefront API sees the
    // components that aren't published to the Online Store, so its list wins.
    const apply = (map) => {
      if (!map || !Object.keys(map).length) return;
      holder.__lrBoxData = Object.assign({}, holder.__lrBoxData, map);
      render(holder, holder.__lrVariantId);
    };

    const cached = readCache(handle);
    if (cached) {
      apply(cached);
      return;
    }

    fetchComponents(handle)
      .then((map) => {
        if (map && Object.keys(map).length) writeCache(handle, map);
        apply(map);
      })
      .catch((err) => console.warn('[LR box contents] fetch failed, keeping server-rendered list', err));
  }

  function init(scope) {
    const host = scope || document;
    const holders = host.matches && host.matches(SELECTORS.holder)
      ? [host]
      : Array.from(host.querySelectorAll(SELECTORS.holder));

    holders.forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  window.lrBoxContents = { init: init };
})();
